import { Typography } from '@mui/material';
import React from 'react';
import BackendService from 'BackendService';
import StyledSection from '../components/styles/StyledSection';
import { CmsBlock, FundUpdateEntry } from '../models/FundUpdateEntry';
import FundUpdatesReadLayout, { type FundUpdateReadEntry } from './FundUpdatesReadLayout';

interface FundUpdatesReadViewProps {
  fundOpportunityId: string;
  onToolbarWidthChange?: (width: number) => void;
}

interface FundUpdatesReadViewState {
  loading: boolean;
  updates: FundUpdateEntry[];
  error: string | null;
}

/**
 * Extracts a date string from a CMS block's properties for legacy multi-block files.
 * Falls back to null if not found.
 */
function blockDate(block: CmsBlock): string | null {
  return (block.properties as any)?.publishedAt || (block.properties as any)?.date || null;
}

/**
 * Read-only view of fund updates for non-admin users.
 *
 * Fetches published updates from the new API and renders each in reverse
 * chronological order with a date header.
 *
 * Legacy updates (is_legacy=true) may contain multiple blocks; each block
 * is rendered as its own entry using its embedded date if available.
 */
export default class FundUpdatesReadView extends React.Component<
  FundUpdatesReadViewProps,
  FundUpdatesReadViewState
> {
  constructor(props: FundUpdatesReadViewProps) {
    super(props);
    this.state = { loading: true, updates: [], error: null };
  }

  componentDidMount() {
    this.loadUpdates();
  }

  private loadUpdates = async () => {
    try {
      const updates = await new BackendService().getPublishedFundUpdates(
        this.props.fundOpportunityId,
      );
      this.setState({ loading: false, updates });
    } catch {
      this.setState({ loading: false, error: 'Unable to load fund updates.' });
    }
  };

  /**
   * For legacy entries: expand each block into its own dated entry.
   * For new entries: single block with the entry's updateDate.
   */
  private expandEntries(): FundUpdateReadEntry[] {
    const entries: FundUpdateReadEntry[] = [];

    for (const update of this.state.updates) {
      entries.push({ date: update.updateDate, title: update.title, blocks: update.content });
    }

    return entries;
  }

  render() {
    const { loading, error } = this.state;

    if (loading) {
      return (
        <StyledSection tone="tone1" sx={{ mt: '1.875rem' }}>
          <Typography>Loading updates…</Typography>
        </StyledSection>
      );
    }

    if (error) {
      return (
        <StyledSection tone="tone1" sx={{ mt: '1.875rem' }}>
          <Typography color="error">{error}</Typography>
        </StyledSection>
      );
    }

    const entries = this.expandEntries();

    if (entries.length === 0) {
      return null;
    }

    return <FundUpdatesReadLayout entries={entries} />;
  }
}
